/**
 * MasterDataTable (Iter 9.39a)
 *
 * Shared Airtable-style table layout for Master databases:
 *   /admin/product/master-services
 *   /admin/product/master-occasions
 *   /admin/product/master-experiences
 *   /admin/product/brands
 *
 * Features:
 *   - Column-based table with sortable headers
 *   - Search bar across configured search fields
 *   - Inline edit toggle (per row, opens edit drawer or inline form)
 *   - Multi-select tag support via `tagFields` config
 *   - Approve / Reject for pending host submissions
 *   - Delete with confirm
 *
 * Each consumer passes:
 *   - columns: [{ key, label, render?, sortable? }]
 *   - rows: data array
 *   - searchFields: ["name", "category", ...]
 *   - onEdit(row), onDelete(row), renderEditForm({row, onSave, onCancel})
 *   - extraTopActions: optional JSX rendered next to "Add"
 */
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUp,
  ArrowDown,
  Loader2,
  Plus,
  Search,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronUp
} from "lucide-react";

export default function MasterDataTable({
  title,
  icon: Icon,
  description,
  loading = false,
  rows = [],
  columns = [],
  searchFields = ["name"],
  testid = "master-table",
  // create form
  renderCreateForm, // ({ onCreated }) => JSX
  // row-level actions
  onEdit, // (row) => void
  onDelete, // (row) => void
  // inline expand row
  renderRowDetail, // (row) => JSX (optional, expandable detail)
  // extras
  extraFilters, // JSX rendered next to search
  extraColumns, // additional cells per row (after columns)
  emptyHint = "No items yet.",
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState({}); // row_id -> bool

  const filtered = useMemo(() => {
    let r = rows;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((row) =>
        searchFields.some((f) => String(row[f] || "").toLowerCase().includes(q)),
      );
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const av = String(a[sortKey] || "").toLowerCase();
        const bv = String(b[sortKey] || "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return r;
  }, [rows, search, searchFields, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const rowKey = (row) => row.id || row._id || row.brand_id || row.service_id || row.occasion_id || row.experience_id || row.master_product_id || row.product_id || JSON.stringify(row).slice(0, 30);

  return (
    <div className="space-y-4" data-testid={testid}>
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        {Icon && <Icon className="w-5 h-5" />}
        <h1 className="text-xl font-bold">{title}</h1>
        <Badge className="bg-purple-100 text-purple-700 border-0">{rows.length}</Badge>
        <div className="flex-1" />
        <Button
          onClick={() => setShowCreate((s) => !s)}
          size="sm"
          className="bg-purple-600 hover:bg-purple-700"
          data-testid={`${testid}-toggle-create`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />{showCreate ? "Cancel" : "Add"}
        </Button>
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}

      {/* Create form */}
      {showCreate && renderCreateForm && (
        <Card data-testid={`${testid}-create-card`}>
          <CardContent className="p-4">
            {renderCreateForm({ onCreated: () => setShowCreate(false) })}
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search by ${searchFields.join(", ")}…`}
            className="pl-8"
            data-testid={`${testid}-search`}
          />
        </div>
        {extraFilters}
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="border-b bg-gray-50/60 text-left">
                {renderRowDetail && <th className="w-8" />}
                {columns.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => c.sortable !== false && setSort(c.key)}
                    className={`px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-medium ${c.sortable !== false ? "cursor-pointer select-none" : ""}`}
                    data-testid={`${testid}-th-${c.key}`}
                  >
                    <div className="inline-flex items-center gap-1">
                      {c.label}
                      {sortKey === c.key && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                ))}
                {(onEdit || onDelete || extraColumns) && <th className="px-3 py-2 text-right" />}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 2} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 2} className="py-8 text-center text-xs text-gray-400">{emptyHint}</td></tr>
              ) : filtered.map((row) => {
                const k = rowKey(row);
                const isOpen = !!expanded[k];
                return (
                  <React.Fragment key={k}>
                    <tr className="border-b hover:bg-gray-50/40" data-testid={`${testid}-row-${k}`}>
                      {renderRowDetail && (
                        <td className="px-2">
                          <button
                            type="button"
                            onClick={() => setExpanded((p) => ({ ...p, [k]: !p[k] }))}
                            className="text-gray-400 hover:text-gray-700"
                            data-testid={`${testid}-row-expand-${k}`}
                          >
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      )}
                      {columns.map((c) => (
                        <td key={c.key} className="px-3 py-2 align-top" data-testid={`${testid}-cell-${c.key}-${k}`}>
                          {c.render ? c.render(row) : <span className="text-gray-700">{row[c.key] ?? ""}</span>}
                        </td>
                      ))}
                      {(onEdit || onDelete || extraColumns) && (
                        <td className="px-3 py-2 text-right align-top">
                          <div className="inline-flex items-center gap-1">
                            {extraColumns && extraColumns(row)}
                            {onEdit && (
                              <button onClick={() => onEdit(row)} className="text-gray-400 hover:text-blue-600" data-testid={`${testid}-edit-${k}`}>
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {onDelete && (
                              <button onClick={() => onDelete(row)} className="text-gray-400 hover:text-red-500" data-testid={`${testid}-delete-${k}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isOpen && renderRowDetail && (
                      <tr className="bg-gray-50/40">
                        <td />
                        <td colSpan={columns.length + (onEdit || onDelete || extraColumns ? 1 : 0)} className="px-3 py-3" data-testid={`${testid}-detail-${k}`}>
                          {renderRowDetail(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
